import * as formRules from '../static/formRules.js';
const { required, positive, validDistinguishedName, validActiveDirectoryName } =
  formRules;
import { fetch } from '../utility/fetch.js';

export const accessLevels = ['Read Only', 'Modify'];

const getExternalExeFormItem = () => {
  return {
    label: 'External executable path',
    type: 'text',
    value: undefined,
    placeholder: 'C:/Program Files/path/to/my.exe',
    hint: 'When populated, the data for this step will be sent to an external executable',
    persistentHint: true,
    showWhenChecked: 'Run with external executable',
    // required but only part of the form when checked
    rules: [required],
  };
};

export const getWorkerTemplateSteps = async () => {
  const steps = await fetch('/AssetReviewApi/GetTemplateSteps');
  for (let type of Object.keys(steps)) {
    for (let formItem of steps[type].formItems) {
      // map from string rule names to the methods
      if (formItem.rules !== undefined) {
        formItem.rules = formItem.rules.map((ruleName) => {
          if (ruleName.includes(':')) {
            const [rule, param] = ruleName.split(':');
            return formRules[rule](param);
          } else {
            return formRules[ruleName];
          }
        });
      }
    }
  }
  return steps;
};

/**
 * These are the steps available to assemble to create a worker template
 */
export const workerTypes = {
  timeDelay: {
    label: 'Time delay',
    formItems: [
      {
        label: 'Delay time (s)',
        type: 'number',
        value: 60,
        rules: [required, positive],
      },
      getExternalExeFormItem(),
    ],
  },
  skipNextStepCondition: {
    label: 'Skip Next Step Condition - Custom Question',
    formItems: [
      {
        label: 'If Custom Question',
        type: 'select',
        items: [],
        onChangeMethod: '',
        rules: [required],
        questions: [],
      },
      {
        label: 'Answer is(any of)',
        type: 'select',
        items: [],
        rules: [required],
      },
      getExternalExeFormItem(),
    ],
  },
  createGroup: {
    label: 'UDM Create group(s)',
    formItems: [
      {
        label: 'Naming convention',
        type: 'text',
        value: 'FS_%%server%%_%%sharenetname%%_%%dirname%%_%%access%%',
        hint: 'FS_%%server%%_%%sharenetname%%_%%dirname%%_%%access%%',
        rules: [required, validActiveDirectoryName],
      },
      {
        label: 'Read-Only %%access%% value',
        type: 'text',
        value: 'RO',
        rules: [required],
      },
      {
        label: 'Modify %%access%% value',
        type: 'text',
        value: 'M',
        rules: [required],
      },
      {
        label: 'Organizational Unit',
        type: 'text',
        value: undefined,
        placeholder: 'OU=My Group Container,DC=DomainName,DC=com',
        rules: [validDistinguishedName, required],
      },
      {
        label: 'Description',
        type: 'text',
        value: undefined,
        placeholder: 'Security group for %%DFS_PATH%%',
        rules: [],
      },
      getExternalExeFormItem(),
    ],
    validation: {
      maxPerTemplate: 1,
    },
  },
  UseExistingGroup: {
    label: 'Use Existing group(s)',
    formItems: [
      {
        label: 'Description:',
        type: 'header',
        value:
          'You will be prompted to select the ReadOnly and Modify groups when you schedule the worker',
        disable: true,
        rows: 1,
        rules: [],
      },
    ],
    validation: {
      maxPerTemplate: 1,
    },
  },
  populateGroup: {
    label: 'UDM Populate group(s)',
    formItems: [getExternalExeFormItem()],
    validation: {
      maxPerTemplate: 1,
    },
  },
  addAccess: {
    label: 'Additional access',
    formItems: [
      {
        label: 'Level of access',
        type: 'select',
        items: ['Full Control', 'Modify', 'Read Only', 'List'],
        rules: [required],
      },
      {
        label: 'Group/Account',
        type: 'autocomplete',
        apiItemSource: '/AssetReviewApi/GetAdditionalAccessAccounts',
        rules: [required],
        hint: 'This account will be granted the selected level of access as part of changing folder permissions',
        persistentHint: true,
      },
      getExternalExeFormItem(),
    ],
  },
  remediateFolder: {
    label: 'Change folder permissions',
    formItems: [
      {
        label:
          'Add security groups permissions, leave permissions for excluded accounts, remove all other permissions',
        type: 'header',
        rules: [],
      },
      {
        label: 'Root folder inheritance',
        type: 'radio-group',
        value: 'Protect',
        items: ['Protect', 'Inherit', 'Do not change'],
        rules: [required],
        hint: 'This will decide what happens to inheritance on the target folder the template is run against',
        persistentHint: true,
      },
      {
        label: 'Push permissions to subfolders',
        type: 'checkbox',
        value: true,
        hint: 'Remove all permissions on subfolders and have them inherit permissions from the root folder',
        persistentHint: true,
        rules: [],
      },
      getExternalExeFormItem(),
    ],
    validation: {
      maxPerTemplate: 1,
    },
  },
  runScript: {
    label: 'Run external executable',
    formItems: [
      {
        ...getExternalExeFormItem(),
        showWhenChecked: undefined,
        hint: 'C:/Program Files/dotnet/dotnet.exe',
        rules: [required],
      },
      {
        label: 'Data to send',
        type: 'textarea',
        hint: '--version',
        rules: [required],
      },
    ],
  },
  runStp: {
    label: 'Custom report to external executable',
    formItems: [
      {
        ...getExternalExeFormItem(),
        showWhenChecked: undefined,
        hint: 'C:/Program Files/dotnet/dotnet.exe',
        rules: [required],
      },
      {
        type: 'header',
        label: 'The STP parameters must be: bigint @campaignAssetId',
        rules: [],
      },
      {
        type: 'text',
        label: 'Stored procedure name',
        hint: 'dbo.stp_EXAMPLE_WorkerTemplateInput',
        rules: [],
      },
    ],
  },
  modifyExistingGroup: {
    label: 'Modify Existing Group - Access Review',
    formItems: [getExternalExeFormItem()],
  },
  modifyExistingGroup_UserList: {
    label: 'Modify Existing Group - Add Users',
    formItems: [
      {
        label: 'Select Users',
        type: 'autocomplete',
        apiItemSource: '/AssetReviewApi/GetAdditionalAccessAccounts',
        hint: 'Search AD users - can add one or a list',
        rules: [required],
        allowMultipleSelection: true,
      },
      getExternalExeFormItem(),
    ],
  },
  modifyExistingGroupCustomQuestion: {
    label: 'Modify Existing Group - Custom Question',
    formItems: [getExternalExeFormItem()],
  },
};

/**
 * Parse the steps for the template back into an array and replace the form
 * validation rules which become null when stringified
 * @param {string} stepsJsonString - The string of template step JSON from the database
 */
export const parseAndReplaceRules = (
  stepsJsonString,
  stepListOverride = undefined
) => {
  let parsedSteps = JSON.parse(stepsJsonString);
  return parsedSteps.map((step) => {
    const stepLabel = step.label;
    const stepSource = stepListOverride || Object.values(workerTypes);
    const originalStep = stepSource.find((t) => t.label === stepLabel);
    if (!originalStep) {
      return step;
    }
    step.formItems = step.formItems.map((item) => {
      if (item.value === 'false') {
        item.value = false;
      }
      const itemLabel = item.label;
      const originalItem = originalStep.formItems.find(
        (i) => i.label === itemLabel
      );
      if (!originalItem) {
        return item;
      }
      let newItem = { ...originalItem, value: item.value };
      newItem.rules = (newItem.rules || []).map(
        (ruleName) => formRules[ruleName] || ruleName
      );
      if (
        newItem.showWhenChecked &&
        item.value !== null &&
        item.value !== undefined
      ) {
        newItem.showWhenCheckedValue = true;
      }
      return newItem;
    });
    return step;
  });
};

/**
 * Get the minimum property value from a list of objects
 * @param {object[]} objectList - A list of object
 * @param {*} property - The property to get the minimum value from
 * @param {*} initValue - An initial value if the list is empty
 */
export const getMin = (objectList, property, initValue = Infinity) => {
  return objectList.reduce((acc, val) => {
    if (acc < val[property]) {
      return acc;
    } else {
      return val[property];
    }
  }, initValue);
};

/**
 * Validate the order of the supplied list of steps
 * @param {object[]} steps - The list of template steps to validate
 */
export const validateOrder = (steps) => {
  const changePermSteps = steps.filter(
    (s) => s.label === workerTypes.remediateFolder.label
  );
  const createGroupSteps = steps.filter(
    (s) => s.label === workerTypes.createGroup.label
  );
  const useExistingGroupSteps = steps.filter(
    (s) => s.label === workerTypes.UseExistingGroup.label
  );
  const addAccessSteps = steps.filter(
    (s) => s.label === workerTypes.addAccess.label
  );
  if (createGroupSteps.length + useExistingGroupSteps.length > 1) {
    return `Only one of '${workerTypes.createGroup.label}' and '${workerTypes.UseExistingGroup.label}' is allowed.`;
  }
  if (changePermSteps.length > 1) {
    return `Only one of '${workerTypes.remediateFolder.label}' is allowed.`;
  }
  if (createGroupSteps.length > 0 && changePermSteps.length > 0) {
    const groupOrder = getMin(createGroupSteps, 'order');
    const permOrder = getMin(changePermSteps, 'order');
    if (groupOrder > permOrder) {
      return `'${workerTypes.createGroup.label}' cannot be after '${workerTypes.remediateFolder.label}'.`;
    }
  }
  if (addAccessSteps.length > 0) {
    return validateAccessStep(addAccessSteps, changePermSteps);
  }
  return true;
};

function validateAccessStep(addAccessSteps, changePermSteps) {
  const minAddAccess = getMin(addAccessSteps, 'order');
  const minPerm = getMin(changePermSteps, 'order');
  if (minPerm < minAddAccess || minPerm === Infinity) {
    return `'${workerTypes.addAccess.label}' must be followed by '${workerTypes.remediateFolder.label}'`;
  }
  return true;
}

/**
 * Validate that there are not too many of any step
 * @param {object[]} steps - The list of template steps to validate
 */
export const validateStepCounts = (steps) => {
  const limits = Object.values(workerTypes).map(({ label, validation }) => {
    if (!validation) {
      validation = {
        maxPerTemplate: Infinity,
      };
    }
    const maxPerTemplate = validation.maxPerTemplate || Infinity;
    return { label, maxPerTemplate };
  });
  for (let { label, maxPerTemplate } of limits) {
    if (steps.filter((s) => s.label === label).length > maxPerTemplate) {
      return `You cannot have more than ${maxPerTemplate} of '${label}'`;
    }
  }
  return true;
};
